import { ANALYTICS_COMPARISONS, ANALYTICS_PERIODS, ANALYTICS_TREND_GRAINS } from '~/types/leLens';

import { GROWTH_ATTRIBUTIONS, GROWTH_PAGES, GROWTH_SOURCES } from './types';
import type { GrowthPageId, GrowthSourceId, GrowthViewState } from './types';

export const GROWTH_PAGE_PARAM = 'growthPage';

/** Every query param the Growth page owns, so the shell can clear them when another page opens. */
export const GROWTH_PARAM_KEYS: ReadonlyArray<string> = [
  GROWTH_PAGE_PARAM,
  'growthSource',
  'attribution',
  'period',
  'compare',
  'from',
  'to',
  'comparisonFrom',
  'comparisonTo',
  'grain',
  'growthMetric',
  'growthBreakdown',
  'growthDetail',
  'growthMarket',
  'growthChannel',
  'growthCampaign',
  'growthSegment',
  'growthPlatform',
  'growthPromoCode',
  'growthPromoType',
  'growthVoucher',
  'growthBrand',
  'growthProduct',
];

export const GROWTH_NAVIGATION: ReadonlyArray<{
  id: GrowthPageId;
  label: string;
  description: string;
  source: GrowthSourceId;
  children?: ReadonlyArray<{ id: GrowthPageId; label: string }>;
}> = [
  {
    id: 'acquisition',
    label: 'Acquisition & performance',
    description: 'Investment, returns and attributed customer demand.',
    source: 'paid_media',
    children: [{ id: 'gha', label: 'Google Hotel Ads' }],
  },
  {
    id: 'lifecycle',
    label: 'Site conversion',
    description: 'Sessions, shopping intent and checkout progression.',
    source: 'sessions',
  },
];

/**
 * Paid channels the rail lists under Growth, largest spend first (MTD Oct 2026: Paid Search 55%, Paid Social 14%).
 * Ids equal the paid_media channel filter ids. "Paid Search (Brand)" is a separate channel. Affiliates has no row;
 * `growthChannel=Affiliates` still opens the acquisition page filtered to it.
 */
export const GROWTH_RAIL_CHANNELS: ReadonlyArray<string> = ['Paid Search', 'Paid Social'];

/** The sessions traffic channel the Direct row is about; also its channel filter id. */
export const GROWTH_DIRECT_CHANNEL = 'Direct';

/** A channel page: the paid media view filtered to one channel, on the acquisition page. */
export function growthChannelState(current: GrowthViewState, channel: string): GrowthViewState {
  const base = navigateGrowthPage(current, 'acquisition');
  return { ...base, filters: { ...base.filters, channel: [channel] } };
}

/** The Direct page: the lifecycle page's sessions view filtered to the Direct traffic channel. */
export function growthDirectState(current: GrowthViewState): GrowthViewState {
  const base = navigateGrowthPage(current, 'lifecycle', 'sessions');
  return { ...base, filters: { ...base.filters, channel: [GROWTH_DIRECT_CHANNEL] } };
}

/** What a rail channel row opens: the Direct row is a sessions preset, every other row is a paid channel. */
export function growthRailChannelState(current: GrowthViewState, channel: string): GrowthViewState {
  return channel === GROWTH_DIRECT_CHANNEL ? growthDirectState(current) : growthChannelState(current, channel);
}

/** The rail channel a Growth state shows, if it is exactly one of GROWTH_RAIL_CHANNELS on the paid media view. */
export function activeGrowthChannel(state: GrowthViewState): string | null {
  const [channel, ...rest] = state.filters.channel;
  return state.page === 'acquisition' && state.source === 'paid_media' && channel && rest.length === 0
    ? (GROWTH_RAIL_CHANNELS.find((id) => id === channel) ?? null)
    : null;
}

/** Whether a Growth state is the Direct page: sessions on the lifecycle page, filtered to exactly Direct. */
export function isGrowthDirect(state: GrowthViewState): boolean {
  const [channel, ...rest] = state.filters.channel;
  return (
    state.page === 'lifecycle' && state.source === 'sessions' && channel === GROWTH_DIRECT_CHANNEL && rest.length === 0
  );
}

/** The channel a channel-preset page is about (a paid rail channel or Direct), which is not one of the reader's filters. */
export function growthPageChannel(state: GrowthViewState): string | null {
  return activeGrowthChannel(state) ?? (isGrowthDirect(state) ? GROWTH_DIRECT_CHANNEL : null);
}

/** Sources a lifecycle row covers: Site conversion reads sessions and the offer funnel, Email reads email. */
export function lifecycleSourcesFor(source: GrowthSourceId): ReadonlyArray<GrowthSourceId> {
  return source === 'email' ? ['email'] : ['sessions', 'offer_funnel'];
}

/** The lifecycle page a Growth state shows, if it is on the lifecycle page. */
export function activeGrowthLifecycle(state: GrowthViewState): 'site-conversion' | 'direct' | 'email' | null {
  return state.page !== 'lifecycle'
    ? null
    : state.source === 'email'
      ? 'email'
      : isGrowthDirect(state)
        ? 'direct'
        : 'site-conversion';
}

export function emptyGrowthFilters(): GrowthViewState['filters'] {
  return {
    market: [],
    channel: [],
    campaign: [],
    segment: [],
    platform: [],
    promoCode: [],
    promoType: [],
    voucher: [],
    brand: [],
    product: [],
  };
}

const DEFAULT_STATE: GrowthViewState = {
  page: 'overview',
  source: 'paid_media',
  attribution: 'operating',
  period: 'month_to_date',
  comparison: 'previous_year',
  filters: emptyGrowthFilters(),
};

function oneOf<T extends string>(value: string | null, values: ReadonlyArray<T>, fallback: T): T {
  return value && values.includes(value as T) ? (value as T) : fallback;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isDateRange(from?: string, to?: string): boolean {
  return Boolean(from && to && ISO_DATE.test(from) && ISO_DATE.test(to) && from <= to);
}

function list(params: URLSearchParams, key: string): Array<string> {
  return (params.get(key) ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function sourceAllowedOnPage(page: GrowthPageId, source: GrowthSourceId): boolean {
  if (page === 'gha') {
    return source === 'gha';
  }
  if (page === 'acquisition') {
    return source === 'paid_media' || source === 'gha' || source === 'promotions';
  }
  if (page === 'lifecycle') {
    return ['sessions', 'offer_funnel', 'email'].includes(source);
  }
  return ['paid_media', 'gha', 'promotions', 'sessions', 'offer_funnel'].includes(source);
}

export function growthStateFromParams(params: URLSearchParams): GrowthViewState {
  const page = oneOf(params.get(GROWTH_PAGE_PARAM), GROWTH_PAGES, DEFAULT_STATE.page);
  const defaultSource = page === 'gha' ? 'gha' : page === 'lifecycle' ? 'sessions' : 'paid_media';
  const requestedSource = oneOf(params.get('growthSource'), GROWTH_SOURCES, defaultSource);
  const source = sourceAllowedOnPage(page, requestedSource) ? requestedSource : defaultSource;
  const requestedAttribution = oneOf(params.get('attribution'), GROWTH_ATTRIBUTIONS, DEFAULT_STATE.attribution);
  const requestedPeriod = oneOf(params.get('period'), ANALYTICS_PERIODS, DEFAULT_STATE.period);
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;
  const period = requestedPeriod === 'custom' && !isDateRange(from, to) ? DEFAULT_STATE.period : requestedPeriod;
  const requestedComparison = oneOf(params.get('compare'), ANALYTICS_COMPARISONS, DEFAULT_STATE.comparison);
  const comparisonFrom = params.get('comparisonFrom') ?? undefined;
  const comparisonTo = params.get('comparisonTo') ?? undefined;
  const comparison =
    requestedComparison === 'custom' && !isDateRange(comparisonFrom, comparisonTo)
      ? DEFAULT_STATE.comparison
      : requestedComparison;
  const focusMetric = params.get('growthMetric');
  return {
    page,
    source,
    attribution: source === 'paid_media' ? requestedAttribution : 'operating',
    period,
    comparison,
    from: period === 'custom' ? from : undefined,
    to: period === 'custom' ? to : undefined,
    comparisonFrom: comparison === 'custom' ? comparisonFrom : undefined,
    comparisonTo: comparison === 'custom' ? comparisonTo : undefined,
    trendGrain: ANALYTICS_TREND_GRAINS.find((grain) => grain === params.get('grain')),
    focusMetric: focusMetric?.startsWith('growth.') ? focusMetric : undefined,
    breakdown: params.get('growthBreakdown') ?? undefined,
    ...(params.get('growthDetail') === '1' ? { detailOpen: true } : {}),
    filters: {
      market: list(params, 'growthMarket'),
      channel: list(params, 'growthChannel'),
      campaign: list(params, 'growthCampaign'),
      segment: list(params, 'growthSegment'),
      platform: list(params, 'growthPlatform'),
      promoCode: list(params, 'growthPromoCode'),
      promoType: list(params, 'growthPromoType'),
      voucher: list(params, 'growthVoucher'),
      brand: list(params, 'growthBrand'),
      product: list(params, 'growthProduct'),
    },
  };
}

export function growthStateToParams(current: URLSearchParams, state: GrowthViewState): URLSearchParams {
  const next = new URLSearchParams(current);
  const write = (key: string, value?: string) => (value ? next.set(key, value) : next.delete(key));
  write(GROWTH_PAGE_PARAM, state.page === 'overview' ? undefined : state.page);
  const defaultSource = state.page === 'gha' ? 'gha' : state.page === 'lifecycle' ? 'sessions' : 'paid_media';
  write('growthSource', state.source === defaultSource ? undefined : state.source);
  write('attribution', state.attribution === 'operating' ? undefined : state.attribution);
  write('period', state.period === DEFAULT_STATE.period ? undefined : state.period);
  write('compare', state.comparison === 'previous_year' ? undefined : state.comparison);
  write('from', state.period === 'custom' ? state.from : undefined);
  write('to', state.period === 'custom' ? state.to : undefined);
  write('comparisonFrom', state.comparison === 'custom' ? state.comparisonFrom : undefined);
  write('comparisonTo', state.comparison === 'custom' ? state.comparisonTo : undefined);
  write('grain', state.trendGrain);
  write('growthMetric', state.focusMetric);
  write('growthBreakdown', state.breakdown);
  write('growthDetail', state.detailOpen ? '1' : undefined);
  write('growthMarket', state.filters.market.join(','));
  write('growthChannel', state.filters.channel.join(','));
  write('growthCampaign', state.filters.campaign.join(','));
  write('growthSegment', state.filters.segment.join(','));
  write('growthPlatform', state.filters.platform.join(','));
  write('growthPromoCode', state.filters.promoCode.join(','));
  write('growthPromoType', state.filters.promoType.join(','));
  write('growthVoucher', state.filters.voucher.join(','));
  write('growthBrand', state.filters.brand.join(','));
  write('growthProduct', state.filters.product.join(','));
  return next;
}

/** Opens a page on its default source, or on `preset` when the page allows it (the Email page, which has no rail row). */
export function navigateGrowthPage(
  current: GrowthViewState,
  page: GrowthPageId,
  preset?: GrowthSourceId,
): GrowthViewState {
  const fallback = page === 'gha' ? 'gha' : page === 'lifecycle' ? 'sessions' : 'paid_media';
  const source = preset && sourceAllowedOnPage(page, preset) ? preset : fallback;
  return {
    ...current,
    page,
    source,
    attribution: source === 'paid_media' ? current.attribution : 'operating',
    focusMetric: undefined,
    breakdown: undefined,
    detailOpen: undefined,
    filters: emptyGrowthFilters(),
  };
}

/**
 * Changes to what the figures cover (period, comparison, attribution, source, filters) are steps the reader can go Back from.
 * Opening or closing Explore and choosing its subject, split or grain only change what is shown of the same figures, so they replace.
 */
export function growthHistoryModeFor(current: GrowthViewState, next: GrowthViewState): 'push' | 'replace' {
  const scopeOf = ({
    detailOpen: _detailOpen,
    focusMetric: _focusMetric,
    breakdown: _breakdown,
    trendGrain: _trendGrain,
    ...scope
  }: GrowthViewState) => JSON.stringify(scope);
  return scopeOf(current) === scopeOf(next) ? 'replace' : 'push';
}
